import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useBlocker } from "react-router-dom";

const DEFAULT_MESSAGE = "Discard unsaved changes?";

interface GuardState {
  isDirty: boolean;
  message: string;
}

interface UnsavedChangesContextValue {
  setGuard: (ownerId: string, dirty: boolean, message?: string) => void;
  confirmLeave: () => boolean;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
  null,
);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [guards, setGuards] = useState<Record<string, GuardState>>({});
  const activeGuard =
    Object.values(guards).find((guard) => guard.isDirty) ?? null;
  // Mirrors the active guard in a ref so confirmLeave (called from event
  // handlers, not render) always reads the latest value without needing to
  // be recreated on every state change.
  const activeGuardRef = useRef(activeGuard);
  activeGuardRef.current = activeGuard;

  const setGuard = useCallback((ownerId: string, dirty: boolean, message?: string) => {
    setGuards((current) => {
      if (!dirty) {
        const { [ownerId]: _removed, ...rest } = current;
        return rest;
      }
      return {
        ...current,
        [ownerId]: { isDirty: true, message: message ?? DEFAULT_MESSAGE },
      };
    });
  }, []);

  const confirmLeave = useCallback(() => {
    if (!activeGuardRef.current) return true;
    return window.confirm(activeGuardRef.current.message);
  }, []);

  useUnsavedNavigationBlocker(Boolean(activeGuard), confirmLeave);

  useEffect(() => {
    if (!activeGuard) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [activeGuard]);

  return (
    <UnsavedChangesContext.Provider value={{ setGuard, confirmLeave }}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

function useUnsavedChangesContext(): UnsavedChangesContextValue {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error(
      "useUnsavedChangesGuard/useGuardedNavClick must be used within UnsavedChangesProvider",
    );
  }
  return ctx;
}

/**
 * Registers this component's dirty state under a stable owner id. Parent and
 * child edit surfaces may render at the same time, so false registrations must
 * not clear another owner's active dirty guard.
 */
export function useUnsavedChangesGuard(isDirty: boolean, message?: string) {
  const { setGuard } = useUnsavedChangesContext();
  const ownerId = useId();

  useEffect(() => {
    setGuard(ownerId, isDirty, message);
    return () => setGuard(ownerId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, isDirty, message]);
}

/**
 * Wraps an onClick handler (typically a Link/NavLink's) with a
 * confirm-before-navigating check against the active guard.
 */
export function useGuardedNavClick(
  onClick?: (e: MouseEvent<HTMLElement>) => void,
) {
  const { confirmLeave } = useUnsavedChangesContext();

  return (e: MouseEvent<HTMLElement>) => {
    if (!confirmLeave()) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };
}

export function useConfirmUnsavedChanges() {
  const { confirmLeave } = useUnsavedChangesContext();
  return confirmLeave;
}

function useUnsavedNavigationBlocker(isActive: boolean, confirmLeave: () => boolean) {
  const blocker = useBlocker(({ historyAction }) => {
    return isActive && historyAction === "POP";
  });

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (confirmLeave()) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, confirmLeave]);
}
